import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { AircraftPicker, ExaminerError, useExaminer } from './ExaminerShared';

const checkrides = [
  ['private', 'Private Airplane'],
  ['instrument', 'Instrument Airplane'],
  ['commercial', 'Commercial Airplane'],
  ['commercial_multi', 'Commercial Multi (uses Commercial Airplane ACS)']
];

const organizationCategories = [
  ['acs', 'FAA Airman Certification Standards (ACS)'],
  ['faa_handbook', 'FAA handbook'],
  ['aim', 'Aeronautical Information Manual (AIM)'],
  ['regulations', 'Regulations (14 CFR)']
];

const aircraftCategories = [
  ['poh_afm', 'POH / AFM'],
  ['equipment_supplement', 'Equipment / STC supplement'],
  ['weight_balance', 'Aircraft-specific weight-and-balance / equipment']
];
const categoryLabels = Object.fromEntries([...organizationCategories, ...aircraftCategories]);

const handbookTitles = [
  'Pilot’s Handbook of Aeronautical Knowledge',
  'Airplane Flying Handbook',
  'Instrument Flying Handbook',
  'Instrument Procedures Handbook',
  'Aviation Weather Handbook',
  'Risk Management Handbook',
  'Aviation Instructor’s Handbook',
  'Other FAA handbook'
];

const emptyDocument = () => ({
  scope: 'organization',
  category: 'acs',
  title: '',
  revision: '',
  effectiveDate: '',
  applicableCheckrides: [],
  acsScope: 'private',
  handbookTitle: handbookTitles[0],
  handbookCustomTitle: '',
  regulatoryCoverage: '',
  sourceUrl: '',
  aircraftId: '',
  serialApplicability: '',
  equipmentApplicability: '',
  supplementApplicability: '',
  actualAircraftLoadingQuestionsEnabled: false
});

const processingStatuses = new Set(['queued', 'processing', 'extracting']);
const checkridesForAcs = {
  private: ['private'],
  instrument: ['instrument'],
  commercial: ['commercial', 'commercial_multi']
};

export default function ExaminerDocumentLibrary() {
  const { aircraft = [] } = useApp();
  const { request, run, busy, error, setError } = useExaminer();
  const [documents, setDocuments] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState(emptyDocument);
  const [replacing, setReplacing] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [resumeAcknowledged, setResumeAcknowledged] = useState(false);
  const [retryAcknowledged, setRetryAcknowledged] = useState({});
  const [message, setMessage] = useState('');
  const formRef = useRef(null);

  const refresh = async () => {
    setReadiness(null);
    setLoaded(false);
    setResumeAcknowledged(false);
    const library = await request('/documents');
    if (!library || !Array.isArray(library.documents)) throw new Error('The examiner document library returned an invalid response.');
    if (!library.readiness || typeof library.readiness !== 'object') throw new Error('The examiner document library response did not include setup readiness.');
    setDocuments(library.documents);
    setReadiness(library.readiness || null);
    setLoaded(true);
    setResumeAcknowledged(false);
  };

  useEffect(() => { run(refresh); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateDraft = (key, value) => setDraft(previous => ({ ...previous, [key]: value }));
  const chooseAircraft = value => {
    const plane = aircraft.find(item => String(item.id) === String(value));
    setDraft(previous => ({ ...previous, aircraftId: value,
      serialApplicability: plane?.serialNumber || plane?.serial_number || '',
      equipmentApplicability: plane?.equipment || '' }));
  };
  const applicableCategories = [...organizationCategories, ...aircraftCategories];
  const selectedCategory = applicableCategories.some(([value]) => value === draft.category)
    ? draft.category : applicableCategories[0][0];
  const isAircraftDocument = draft.scope === 'aircraft';
  const resume = readiness?.resumeExistingSetup;
  const documentsProcessing = documents.some(document => processingStatuses.has(String(document.processingStatus || document.status || '').toLowerCase()));
  const missingDocuments = readiness?.missingDocuments || readiness?.missing || [];
  const replacementDocument = documents.find(document => String(document.id) === String(replacing));

  const selectReplacement = id => {
    formRef.current?.reset();
    setMessage('');
    setError(null);
    const document = documents.find(item => String(item.id) === id);
    setReplacing(document?.id ?? null);
    if (!document) {
      setDraft(emptyDocument());
      setAuthorized(false);
      return;
    }
    setDraft({
      ...emptyDocument(),
      scope: document.aircraftId ? 'aircraft' : 'organization',
      category: document.category,
      title: document.title || '',
      revision: '',
      effectiveDate: '',
      applicableCheckrides: document.applicableCheckrides || [],
      acsScope: checkridesForAcs[document.acsScope] ? document.acsScope : 'private',
      handbookTitle: document.handbookTitle || handbookTitles[0],
      handbookCustomTitle: document.handbookCustomTitle || '',
      regulatoryCoverage: document.regulatoryCoverage || '',
      sourceUrl: document.sourceUrl || '',
      aircraftId: document.aircraftId || '',
      serialApplicability: document.serialApplicability || '',
      equipmentApplicability: document.equipmentApplicability || '',
      supplementApplicability: document.supplementApplicability || '',
      actualAircraftLoadingQuestionsEnabled: document.actualAircraftLoadingQuestionsEnabled === true
    });
    setAuthorized(false);
  };

  const submit = event => {
    event.preventDefault();
    run(async () => {
      setMessage('');
      setError(null);
      const file = formRef.current?.elements.namedItem('document')?.files?.[0];
      if (!(file instanceof File) || !file.size || file.size > 64 * 1024 * 1024) throw new Error('Select a nonempty PDF or plain-text document no larger than 64 MiB.');
      if (!/(\.pdf|\.txt)$/i.test(file.name)) throw new Error('Select a PDF or plain-text document.');
      const selectedCheckrides = selectedCategory === 'acs' ? checkridesForAcs[draft.acsScope] : draft.applicableCheckrides;
      const title = draft.title.trim() || file.name.replace(/\.(pdf|txt)$/i, '');
      if (!title || !draft.revision.trim() || !selectedCheckrides?.length) throw new Error('Enter the revision printed on the document and select at least one applicable checkride.');
      if (replacing && replacementDocument?.revision === draft.revision.trim()) throw new Error('Enter a newer revision or edition than the one currently stored.');
      if (isAircraftDocument && !draft.aircraftId) throw new Error('Select the aircraft this document applies to.');
      if (selectedCategory === 'poh_afm' && !draft.serialApplicability.trim()) throw new Error('Enter the applicable aircraft serial number or range for this POH/AFM.');
      if (selectedCategory === 'equipment_supplement' && !draft.equipmentApplicability.trim() && !draft.supplementApplicability.trim()) {
        throw new Error('Enter the applicable equipment or supplement information.');
      }
      if (selectedCategory === 'weight_balance' && draft.actualAircraftLoadingQuestionsEnabled
        && (!draft.serialApplicability.trim() || !draft.equipmentApplicability.trim())) {
        throw new Error('Enter the aircraft serial applicability and equipment details used for actual-aircraft loading questions.');
      }
      if (draft.scope === 'organization' && selectedCategory === 'regulations' && !draft.regulatoryCoverage.trim()) {
        throw new Error('Identify the included 14 CFR parts or sections.');
      }
      if (!authorized) throw new Error('Confirm upload authorization and document-processing consent.');
      if (draft.sourceUrl.trim()) {
        let url;
        try { url = new URL(draft.sourceUrl.trim()); } catch { throw new Error('Enter a valid source URL or leave it blank.'); }
        if (!['http:', 'https:'].includes(url.protocol)) throw new Error('Source URL must use HTTP or HTTPS.');
      }

      const body = new FormData();
      body.append('document', file);
      body.append('scope', draft.scope);
      body.append('category', selectedCategory);
      body.append('title', title);
      body.append('revision', draft.revision.trim());
      body.append('effectiveDate', draft.effectiveDate);
      body.append('applicableCheckrides', JSON.stringify(selectedCheckrides));
      body.append('acsScope', selectedCategory === 'acs' ? draft.acsScope : '');
      body.append('handbookTitle', selectedCategory === 'faa_handbook' ? draft.handbookTitle : '');
      body.append('handbookCustomTitle', selectedCategory === 'faa_handbook' ? draft.handbookCustomTitle.trim() : '');
      body.append('regulatoryCoverage', selectedCategory === 'regulations' ? draft.regulatoryCoverage.trim() : '');
      body.append('sourceUrl', draft.sourceUrl.trim());
      body.append('aircraftId', isAircraftDocument ? String(draft.aircraftId) : '');
      body.append('serialApplicability', isAircraftDocument ? draft.serialApplicability.trim() : '');
      body.append('equipmentApplicability', isAircraftDocument ? draft.equipmentApplicability.trim() : '');
      body.append('supplementApplicability', isAircraftDocument ? draft.supplementApplicability.trim() : '');
      body.append('actualAircraftLoadingQuestionsEnabled', String(isAircraftDocument && draft.actualAircraftLoadingQuestionsEnabled));
      body.append('authorizedToUpload', 'true');
      body.append('processingConsent', 'true');
      const path = replacing ? `/documents/${replacing}/replace` : '/documents';
      await request(path, { method: 'POST', body });
      formRef.current.reset();
      setDraft(emptyDocument());
      setReplacing(null);
      setAuthorized(false);
      setMessage('Document uploaded. Its processing status and setup readiness have been refreshed. No FAA download or setup job was started.');
      await refresh();
    });
  };

  const resumeSetup = () => run(async () => {
    setMessage('');
    const result = await request('/initialization/resume', {
      method: 'POST',
      body: { retryAcknowledged: resume?.acknowledgmentRequired ? resumeAcknowledged : true }
    });
    if (!Number.isSafeInteger(result.resumedPlans)) throw new Error('The setup-resume response did not include a valid resumed-plan count.');
    setMessage(`Resumed ${result.resumedPlans} existing setup plan${result.resumedPlans === 1 ? '' : 's'}. No duplicate setup jobs were created.`);
    await refresh();
  });

  return <section aria-label="AI Examiner Document Library">
    <h3>AI Examiner Document Library</h3>
    <p>Choose a document type and upload its PDF. Organization documents are shared across aircraft, so you only upload them once.</p>
    <p>Live practice checks sources for your selected checkride and aircraft in Training. It does not require bulk answer generation. Existing setup/resume controls below are for the separate reviewed-curriculum path.</p>
    <ExaminerError error={error} />
    {message && <p role="status">{message}</p>}
    <div className="examiner-actions">
      <button type="button" disabled={busy} onClick={() => run(refresh)}>Refresh documents and readiness</button>
    </div>
    {!loaded && !error && <p role="status">Loading document library...</p>}

    {loaded && <section aria-label="Document readiness">
      <h4>Readiness</h4>
      {readiness?.status && <p role="status">Examiner readiness: <strong>{readiness.status}</strong></p>}
      {documentsProcessing && <p role="status">Document processing is in progress. Refresh documents and readiness after it finishes; no extraction or setup is automatically retried.</p>}
      {!!readiness?.blockers?.length && <ul>{readiness.blockers.map((item, index) => <li key={`${item.code}-${index}`}>{item.message}</li>)}</ul>}
      {!!missingDocuments.length && <>
        <p>Missing required documents:</p>
        <ul>{missingDocuments.map((item, index) => <li key={`${item.name || item.document || item}-${index}`}>
          {typeof item === 'string' ? item : item.name || item.document || item.message || 'Required document'}
          {item.aircraftId ? ` — aircraft ${aircraft.find(plane => Number(plane.id) === Number(item.aircraftId))?.tailNumber || item.aircraftId}` : ''}
        </li>)}</ul>
      </>}
      {resume?.available && <div>
        {documentsProcessing && <p role="status">Wait for document processing to finish, then refresh readiness before resuming setup.</p>}
        {resume.acknowledgmentRequired && <label className="examiner-check">
          <input type="checkbox" checked={resumeAcknowledged} onChange={event => setResumeAcknowledged(event.target.checked)} />
          {resume.acknowledgmentText || 'I acknowledge that resuming existing setup may incur processing costs.'}
        </label>}
        <button type="button" disabled={busy || documentsProcessing || (resume.acknowledgmentRequired && !resumeAcknowledged)} onClick={resumeSetup}>
          Resume existing setup
        </button>
      </div>}
    </section>}

    <section aria-label="Uploaded examiner documents">
      <h4>Uploaded documents</h4>
      {!documents.length && loaded && <p>No documents have been uploaded for this organization.</p>}
      <ul>{documents.map(document => <li key={document.id} className="examiner-source">
        <strong>{document.title || 'Untitled document'}</strong> — {categoryLabels[document.category] || document.category}; revision {document.revision || 'not set'}
        {document.effectiveDate && `; effective ${document.effectiveDate}`}
        <div>Processing: {document.processingStatus || document.status || 'status unavailable'}</div>
        {!!document.ocrPagesSaved && <div>Local OCR pages saved: {document.ocrPagesSaved}. Completed pages are reused when extraction is retried.</div>}
        {!!document.ocrReviewPages?.some(page => page.confidence < 80) && <p role="alert">Incomplete OCR: pages {document.ocrReviewPages.filter(page => page.confidence < 80).map(page => page.pageNumber).join(', ')} are excluded from generation sources. Other readable pages may support instructor-review drafts, but excluded values or diagrams must not be used.</p>}
        {!!document.ocrReviewPages?.length && <p role="alert">OCR pages requiring careful instructor verification: {document.ocrReviewPages.map(page => `${page.pageNumber} (${Math.round(page.confidence)}% confidence)`).join(', ')}. OCR confidence does not verify aircraft limitations, numbers, or table accuracy.</p>}
        {document.active === false && document.processingStatus === 'ready' && <div>Superseded revision — retained for history, not used for new setup.</div>}
        {document.validUntil && <div>Source validity: {document.validUntil}</div>}
        {document.aircraftId && <div>Aircraft: {aircraft.find(plane => Number(plane.id) === Number(document.aircraftId))?.tailNumber || document.aircraftId}</div>}
        {document.acsScope && <div>ACS: {document.acsScope === 'commercial' ? 'Commercial Airplane (including Commercial Multi)' : document.acsScope === 'instrument' ? 'Instrument Airplane' : 'Private Airplane'}</div>}
        {document.handbookTitle && <div>FAA handbook: {document.handbookCustomTitle || document.handbookTitle}</div>}
        {!!document.applicableCheckrides?.length && <div>Checkrides: {document.applicableCheckrides.map(value => checkrides.find(([id]) => id === value)?.[1] || value).join(', ')}</div>}
        {document.applicability && <div>Applicability: {document.applicability}</div>}
        {document.serialApplicability && <div>Serial applicability: {document.serialApplicability}</div>}
        {document.equipmentApplicability && <div>Equipment applicability: {document.equipmentApplicability}</div>}
        {document.supplementApplicability && <div>Supplement applicability: {document.supplementApplicability}</div>}
        {document.actualAircraftLoadingQuestionsEnabled && <div>Used for actual-aircraft loading questions</div>}
        {document.regulatoryCoverage && <div>Included regulations: {document.regulatoryCoverage}</div>}
        {document.sourceUrl && <div>Original source URL (metadata only): {document.sourceUrl}</div>}
        {(Array.isArray(document.extractionErrors) ? document.extractionErrors : Array.isArray(document.errors) ? document.errors : []).map((item, index) => <p role="alert" className="examiner-error" key={index}>
          {typeof item === 'string' ? item : `${item.code || 'Extraction error'}: ${item.message || item.error || 'Unable to extract document text.'}`}
        </p>)}
        {(document.processingStatus === 'failed' || document.reprocessingRequired) && <>
          {document.reprocessingRequired && <p role="alert">Earlier whole-document OCR needs page-by-page reprocessing before this document can support setup.</p>}
          <p>The file is already saved. Retry this upload after resolving the error; do not upload it again.</p>
          <label className="examiner-check"><input type="checkbox" checked={!!retryAcknowledged[document.id]} onChange={event => {
            const checked = event.target.checked;
            setRetryAcknowledged(previous => ({ ...previous, [document.id]: checked }));
          }} />I acknowledge reprocessing this saved file. PDF page OCR runs locally without OpenAI API charges; resuming examiner generation is a separate action.</label>
          <button type="button" disabled={busy || !retryAcknowledged[document.id]} onClick={() => run(async () => {
            await request(`/documents/${document.id}/retry`, { method: 'POST', body: { retryAcknowledged: true } });
            setRetryAcknowledged(previous => ({ ...previous, [document.id]: false }));
            setMessage('Extraction retry queued for this document. No duplicate upload or setup job was created.');
            await refresh();
          })}>Retry document extraction</button>
        </>}
        {document.active !== false && <button type="button" disabled={busy} onClick={() => selectReplacement(String(document.id))}>Replace with newer revision</button>}
      </li>)}</ul>
    </section>

    <section aria-label="Upload examiner document">
      <h4>{replacing ? `Replace ${replacementDocument?.title || 'document'} with a newer revision` : 'Upload a document'}</h4>
      {replacing && <button type="button" onClick={() => selectReplacement('')}>Cancel replacement</button>}
      <form ref={formRef} onSubmit={submit}>
        <fieldset disabled={busy}>
          <label>Document category<select disabled={!!replacing} value={selectedCategory} onChange={event => {
            const category = event.target.value;
            setDraft(previous => ({ ...previous, category,
              scope: aircraftCategories.some(([value]) => value === category) ? 'aircraft' : 'organization' }));
          }}>
            {applicableCategories.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select></label>
          <label>File (PDF or plain text, maximum 64 MiB)
            <input name="document" type="file" accept=".pdf,.txt,application/pdf,text/plain" required />
          </label>
          {isAircraftDocument && <AircraftPicker disabled={!!replacing} aircraft={aircraft} value={draft.aircraftId} onChange={chooseAircraft} />}
          {selectedCategory === 'acs' && <label>ACS
            <select disabled={!!replacing} value={draft.acsScope} onChange={event => updateDraft('acsScope', event.target.value)}>
              <option value="private">Private Airplane</option>
              <option value="instrument">Instrument Airplane</option>
              <option value="commercial">Commercial Airplane (also used for Commercial Multi)</option>
            </select>
          </label>}
          {selectedCategory === 'faa_handbook' && <label>FAA handbook title
            <select value={draft.handbookTitle} onChange={event => updateDraft('handbookTitle', event.target.value)}>
              {handbookTitles.map(title => <option key={title} value={title}>{title}</option>)}
            </select>
          </label>}
          {selectedCategory === 'faa_handbook' && draft.handbookTitle === 'Other FAA handbook' && <label>Other FAA handbook title
            <input required value={draft.handbookCustomTitle} onChange={event => updateDraft('handbookCustomTitle', event.target.value)} />
          </label>}
          {selectedCategory === 'regulations' && <label>Included 14 CFR parts / sections
            <textarea required value={draft.regulatoryCoverage} onChange={event => updateDraft('regulatoryCoverage', event.target.value)} />
          </label>}
          {selectedCategory === 'acs'
            ? <p>Applicable checkrides: {checkridesForAcs[draft.acsScope].map(value => checkrides.find(([id]) => id === value)[1]).join(', ')}</p>
            : <fieldset>
              <legend>Applicable checkrides</legend>
              <button type="button" onClick={() => updateDraft('applicableCheckrides', checkrides.map(([value]) => value))}>All checkrides</button>
              {checkrides.map(([value, label]) => <label className="examiner-check" key={value}>
                <input type="checkbox" checked={draft.applicableCheckrides.includes(value)} onChange={event => updateDraft('applicableCheckrides', event.target.checked
                  ? [...draft.applicableCheckrides, value]
                  : draft.applicableCheckrides.filter(item => item !== value))} />
                {label}
              </label>)}
            </fieldset>}
          {isAircraftDocument && <>
            <label>Aircraft serial-number applicability<input value={draft.serialApplicability} onChange={event => updateDraft('serialApplicability', event.target.value)} placeholder="Serial number or range" /></label>
            <label>Equipment applicability<input value={draft.equipmentApplicability} onChange={event => updateDraft('equipmentApplicability', event.target.value)} placeholder="Installed equipment covered, if applicable" /></label>
            {selectedCategory === 'weight_balance' && <label className="examiner-check">
              <input type="checkbox" checked={draft.actualAircraftLoadingQuestionsEnabled} onChange={event => updateDraft('actualAircraftLoadingQuestionsEnabled', event.target.checked)} />
              Actual-aircraft loading questions are enabled for this setup
            </label>}
          </>}
          <label>Revision / edition<input required value={draft.revision} onChange={event => updateDraft('revision', event.target.value)} /></label>
          <p>Use the revision printed on the document. The file name becomes its title.</p>
          <details>
          <summary>Optional document details</summary>
          <label>Document title<input value={draft.title} onChange={event => updateDraft('title', event.target.value)} placeholder="Use file name" /></label>
          <label>Effective date<input type="date" value={draft.effectiveDate} onChange={event => updateDraft('effectiveDate', event.target.value)} /></label>
          <label>Original official source URL (optional, metadata only)
            <input type="url" value={draft.sourceUrl} onChange={event => updateDraft('sourceUrl', event.target.value)} placeholder="https://..." />
          </label>
          {isAircraftDocument && <label>Supplement applicability<input value={draft.supplementApplicability} onChange={event => updateDraft('supplementApplicability', event.target.value)} placeholder="Applicable STC / equipment supplements" /></label>}
          </details>
          <label className="examiner-check"><input type="checkbox" checked={authorized} onChange={event => setAuthorized(event.target.checked)} />
            I am authorized to upload this document and consent to storing and processing it for examiner setup.
          </label>
          <button type="submit" disabled={!authorized || (replacing && !replacementDocument)}>
            {replacing ? 'Upload newer revision' : 'Upload document'}
          </button>
        </fieldset>
      </form>
    </section>
  </section>;
}
